import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useRef, useEffect, useMemo } from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { omit, arrayMove } from '@elliemae/ds-utilities/utils';
import { mergeRefs } from '@elliemae/ds-utilities/system';
import createInstancePlugin from '@elliemae/ds-shared/createDataInstance/createInstancePlugin';
import { addDragAndDropToColumn } from './decorateGridWithDndColumns.js';
import '@elliemae/ds-icons/GripperVertical';
import '@elliemae/ds-shared/useDataGrid/initColumnDefinition';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var keysToOmit = ['axis', 'contentWindow', 'disableAutoscroll', 'distance', 'getContainer', 'getHelperDimensions', 'helperClass', 'helperContainer', 'hideSortableGhost', 'keyboardSortingTransitionDuration', 'lockAxis', 'lockOffset', 'lockToContainerEdges', 'onSortEnd', 'onSortMove', 'onSortOver', 'onSortStart', 'pressDelay', 'pressThreshold', 'shouldCancelStart', 'transitionDuration', 'updateBeforeSortStart', 'useDragHandle', 'useWindowAsScrollContainer'];

var decorateColumn = function decorateColumn(column, grid) {
  return column.isUserColumn ? addDragAndDropToColumn(grid, column) : column;
};

var getHeaderRowProps = function getHeaderRowProps(props, grid) {
  var _grid$props = grid.props,
      onMoveColumnStart = _grid$props.onMoveColumnStart,
      onMoveColumnEnd = _grid$props.onMoveColumnEnd,
      onMoveColumnOver = _grid$props.onMoveColumnOver;
  var headerRowRef = useRef();
  var columnDraggingOffset = useRef(0);
  var sortedColumns = useRef(grid.getInstance().decoratedColumns);
  var nodeRef = useRef();
  useEffect(function () {
    sortedColumns.current = grid.getInstance().decoratedColumns;
  });

  var onSortOver = function onSortOver(params) {
    var index = params.index,
        newIndex = params.newIndex,
        oldIndex = params.oldIndex;
    var oldColumn = sortedColumns.current[oldIndex];
    var newColumn = sortedColumns.current[newIndex];
    if (!newColumn.isUserColumn || !oldColumn.isUserColumn) return;
    var columnWidth = nodeRef.current.offsetWidth;
    var newColumnWidth = headerRowRef.current.childNodes.item(newColumn.index).offsetWidth;
    var forward = newIndex > oldIndex; // eslint-disable-next-line no-nested-ternary

    var columnTranslate = forward ? newIndex > index ? columnWidth * -1 : 0 : newIndex < index ? columnWidth : 0;
    columnDraggingOffset.current += forward ? newColumnWidth : newColumnWidth * -1;
    newColumn.updateStyle({
      transitionDuration: '300ms',
      transform: "translate3d(".concat(columnTranslate, "px, 0, 0)")
    });
    oldColumn.updateStyle({
      transitionDuration: '300ms',
      transform: "translate3d(".concat(columnDraggingOffset.current, "px, 0, 0)")
    });
    sortedColumns.current = arrayMove(sortedColumns.current, oldIndex, newIndex);
    onMoveColumnOver(params);
  };

  var onSortStart = function onSortStart(params) {
    var node = params.node;
    grid.disableEvents();
    nodeRef.current = node;
    onMoveColumnStart(params);
  };

  var onSortEnd = function onSortEnd(params) {
    var oldIndex = params.oldIndex,
        newIndex = params.newIndex;
    grid.enableEvents();
    columnDraggingOffset.current = 0;
    sortedColumns.current = grid.decoratedColumns;
    grid.decoratedColumns.forEach(function (column) {
      if (!column.updateStyle) return;
      column.updateStyle({
        transitionDuration: null,
        transform: null
      });
    });
    var originalOldColumnIndex = grid.columns.findIndex(function (column) {
      return column.property === grid.decoratedColumns[oldIndex].property;
    });
    var originalNewColumnIndex = grid.columns.findIndex(function (column) {
      return column.property === grid.decoratedColumns[newIndex].property;
    });
    onMoveColumnEnd({
      newIndex: originalNewColumnIndex,
      oldIndex: originalOldColumnIndex
    });
  };

  return {
    axis: 'x',
    helperClass: 'header-dnd-column',
    innerRef: mergeRefs(props.innerRef, headerRowRef),
    lockAxis: 'x',
    onSortEnd: onSortEnd,
    onSortOver: onSortOver,
    onSortStart: onSortStart,
    useDragHandle: true
  };
};

var decorateRenderers = function decorateRenderers(renderers) {
  var RowComponent = renderers.header.row;
  var CellComponent = renderers.header.cell;
  var Row = useMemo(function () {
    return SortableContainer(function (props) {
      return /*#__PURE__*/React.createElement(RowComponent, omit(props, keysToOmit));
    });
  }, []);
  var Cell = useMemo(function () {
    return SortableElement(CellComponent);
  }, []);
  return _objectSpread(_objectSpread({}, renderers), {}, {
    header: _objectSpread(_objectSpread({}, renderers.header), {}, {
      row: Row,
      cell: Cell
    })
  });
};

var DndColumnsPlugin = createInstancePlugin('dnd-columns', {
  getHeaderRowProps: getHeaderRowProps,
  decorateColumn: decorateColumn,
  decorateRenderers: decorateRenderers
});

export { DndColumnsPlugin };
//# sourceMappingURL=DndColumnsPlugin.js.map
